import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Chip, Heading, TextareaField, Text, ResolvedFieldProps } from '@frontend/design-system';
import { AiIconChip } from '../../components/ai-icon-chip';

type CancellationPolicyTextProps = {
  fieldProps: ResolvedFieldProps<'text'>;
  value: string;
};

const MAX_LENGTH = 1000;

export const CancellationPolicyText = ({ fieldProps, value }: CancellationPolicyTextProps) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <>
      <section css={containerStyle}>
        <Heading as='h3' level={3}>
          {t('Patient-Facing Policy Text')}
        </Heading>
        <div css={chipContainerStyle}>
          <Chip
            variant='warn'
            leftElement={<Icon name='computer' />}
            css={{ width: 'max-content', maxWidth: 'max-content' }}
          >
            {t('Appears on Booking Page')}
          </Chip>
          <AiIconChip iconName='ai-regenerate-small' text={t('AI can see')} />
        </div>
      </section>
      <Text>
        {t(
          'Explain your policy to patients. This text appears on your public booking page and is used by the AI Receptionist during patient conversations.'
        )}
      </Text>
      <TextareaField
        {...fieldProps}
        name='cancellationPolicyText'
        label=''
        value={value}
        css={{ marginTop: 'var(--fabric-space-gap-lg)' }}
        maxLength={MAX_LENGTH}
        spellCheck
      />
      <Text color='subdued' css={characterCountStyle}>
        {t('{{current}} / {{max}}', { current: value.length.toLocaleString(), max: MAX_LENGTH.toLocaleString() })}
      </Text>
    </>
  );
};

const containerStyle = css({
  display: 'flex',
  gap: 'var(--fabric-space-gap-sm)',
  marginBottom: 'var(--fabric-space-gap-sm)',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const chipContainerStyle = css({
  display: 'flex',
  gap: 'var(--fabric-space-gap-sm)',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const characterCountStyle = css({
  marginTop: 'var(--fabric-space-gap-sm)',
  textAlign: 'right',
});
